<template>
    <div
        class="workspace_main_content"
        :class="{
            collapsed_chat_list:
                !isMobile && collapseActvToggleState,
            compact_chat_list:
                !isMobile && compactChatListState,
            mobile_chat_open:
                isMobile && isMobileChatRoomSelected
        }"
    >
        <aside
            id="sidepanel"
            @transitionend="handleSidebarTransitionEnd"
        >
            <!--
                Keep the header transition independent from the room-list
                transition. The key is intentional: Vue fades the old layout
                out before mounting the other layout, so the header never
                visibly jumps between expanded and compact markup.
            -->
            <transition
                name="chat-list-header-mode"
                mode="out-in"
            >
                <ChatListHeader
                    :key="
                        headerCollapseState
                            ? 'header-compact'
                            : 'header-expanded'
                    "
                    class="workspace_chat_list_header"
                    :can-create-group="canCreateTeamsAndGroups"
                    :connection-request-count="connectionRequestCount"

                    :is-mobile="isMobile"
                    :collapsed="headerCollapseState"
                    :selected-room-info="chatRoomInfo"
                    @create-group="createGroupPopupEnabledFun"
                    @show-package-access="
                        accessFeaturesOfPackageMethod
                    "
                    @open-tracker="
                        individualActivityTrackerOpenMethod
                    "
                    @open-mobile-tools="mobileToolbarEnabledMethod"
                    @block="blockedUserMethod"
                />
            </transition>

            <!-- Initial chat-room loading shell -->
            <ChatListSkeleton
                v-if="isInitialChatLoading"
                :collapsed="
                    !isMobile && collapseActvToggleState
                "
                :compact="
                    !isMobile && compactChatListState
                "
            />

            <template v-else-if="hasChatRooms">
                <!-- Search -->
                <div
                    v-click-outside="outsideClickOfSearchFieldMethod"
                    class="user_search_for_new_chat"
                    :class="{
                        search_enabled: isSearchEnabled
                    }"
                >
                    <div class="search_for_new_chat_inner">
                        <button
                            type="button"
                            class="search_icon_btn search_back_btn"
                            aria-label="Close search"
                            @click="searchBarForChatClosedFun"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                aria-hidden="true"
                            >
                                <path
                                    fill="currentColor"
                                    d="M12 4l1.4 1.4L7.8 11H20v2H7.8l5.6 5.6L12 20l-8-8 8-8z"
                                />
                            </svg>
                        </button>

                        <span
                            class="search_icon"
                            aria-hidden="true"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                            >
                                <path
                                    fill="currentColor"
                                    d="M15.009 13.805h-.636l-.22-.219a5.184
                                       5.184 0 0 0 1.256-3.386 5.207
                                       5.207 0 1 0-5.207 5.208 5.183
                                       5.183 0 0 0 3.385-1.255l.221.22v.635
                                       l4.004 3.999 1.194-1.195-3.997-4.007
                                       zm-4.808 0a3.605 3.605 0 1 1 0-7.21
                                       3.605 3.605 0 0 1 0 7.21z"
                                />
                            </svg>
                        </span>

                        <input
                            v-model.trim="searchTerm"
                            type="search"
                            class="form-control"
                            placeholder="Search or start new chat"
                            autocomplete="off"
                            aria-label="Search or start new chat"
                            @input="debouncedSearch"
                            @focus="handleSearchbarFocus"
                            @keydown.esc="searchBarForChatClosedFun"
                        >

                        <button
                            v-if="showSearchClearButton"
                            type="button"
                            class="search_icon_btn search_clear_btn"
                            aria-label="Clear search"
                            @click="searchBarForChatClosedFun"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 329.26933 329"
                                width="15"
                                height="15"
                                aria-hidden="true"
                            >
                                <g fill="currentColor">
                                    <path
                                        d="m21.339844 329.398438c-5.460938
                                           0-10.925782-2.089844-15.082032-6.25
                                           -8.34375-8.339844-8.34375-21.824219
                                           0-30.164063l286.589844-286.59375
                                           c8.339844-8.339844 21.824219-8.339844
                                           30.164063 0 8.34375 8.339844
                                           8.34375 21.824219 0 30.164063
                                           l-286.589844 286.59375
                                           c-4.183594 4.179687-9.621094
                                           6.25-15.082031 6.25z"
                                    />

                                    <path
                                        d="m307.929688 329.398438c-5.460938
                                           0-10.921876-2.089844-15.082032-6.25
                                           l-286.589844-286.59375
                                           c-8.34375-8.339844-8.34375-21.824219
                                           0-30.164063 8.339844-8.339844
                                           21.820313-8.339844 30.164063 0
                                           l286.589844 286.59375
                                           c8.34375 8.339844 8.34375 21.824219
                                           0 30.164063-4.160157 4.179687
                                           -9.621094 6.25-15.082031 6.25z"
                                    />
                                </g>
                            </svg>
                        </button>
                    </div>
                </div>

                <!-- Chat list -->
                <div
                    id="contacts_list"
                    ref="chatList"
                    @scroll.passive="handleScroll"
                >
                    <!-- Search results -->
                    <ChatList
                        v-if="userSearchListTabs"
                        :items="chatSearchItemList"
                        list-type="search"
                        :search-result-loaded="searchResultLoaded"
                        :searching-text="searchingContactText"
                        :typing-user-info="typingUserInfoState"
                        :selected-chat-room="chatRoomInfo"
                        :collapse-active="
                            !isMobile && compactChatListState
                        "
                        :active-call-user-id="activeCallUserId"
                        :is-calling="isCalling"
                        @select-chat-room="selectChatRoomForMessageMethod"
                        @video-call="handleSearchVideoCall"
                    />

                    <!-- Normal chat list -->
                    <template v-else>
                        <div
                            v-if="hasChatRooms"
                            class="user_list_for_chat"
                        >
                            <ChatList
                                v-if="pinnedChatRoomList.length > 0"
                                :items="pinnedChatRoomList"
                                list-type="pinned"
                                :typing-user-info="typingUserInfoState"
                                :selected-chat-room="chatRoomInfo"
                                :collapse-active="
                                    !isMobile && compactChatListState
                                "
                                :active-call-user-id="activeCallUserId"
                                :is-calling="isCalling"
                                @select-chat-room="selectChatRoomForMessageMethod"
                                @video-call="handleSearchVideoCall"
                            />

                            <ChatList
                                v-if="fullChatRoomListResult.length > 0"
                                :items="fullChatRoomListResult"
                                list-type="normal"
                                :typing-user-info="typingUserInfoState"
                                :selected-chat-room="chatRoomInfo"
                                :collapse-active="
                                    !isMobile && compactChatListState
                                "
                                :active-call-user-id="activeCallUserId"
                                :is-calling="isCalling"
                                @select-chat-room="selectChatRoomForMessageMethod"
                                @video-call="handleSearchVideoCall"
                            />
                        </div>

                        <div
                            v-else
                            class="chat_list_empty_state"
                        >
                            No conversations found.
                        </div>
                    </template>

                    <div
                        v-if="chatListAPILoader"
                        class="chat_list_loader"
                        aria-label="Loading more chats"
                    ></div>
                </div>
            </template>

            <!-- Loaded empty state -->
            <div
                v-else
                class="chat_list_empty_state chat_list_empty_state--full"
            >
                No conversations available.
            </div>
        </aside>

        <!-- Center loading shell -->
        <ChatInfoSkeleton
            v-if="isInitialChatLoading && !isMobile"
            class="workspace_chat_info_panel"
        />

        <!-- Selected chat -->
        <user-chat-info
            v-else-if="
                hasChatRooms &&
                chatRoomInfo &&
                (!isMobile || isMobileChatMounted)
            "
            class="workspace_chat_info_panel"
            :selected-chat-room-data="chatRoomInfo"
            @forward-room-change="selectChatRoomForMessageMethod"
        />

        <!-- Welcome screen -->
        <WelcomeWorkspaceBlurb
            v-else-if="isChatRoomLoaded && !isMobile"
        />

        <!-- Popups -->
        <create-group-teams
            v-if="createGroupPopup"
        />

        <user-blocked-popup v-if="blockSelectUser" :blockedUserId="blockedUserId"
        :blockedUserEmail="blockedUserEmail" />

        <access-feature-of-blurb
            v-if="accessFeaturesOfPackageState"
        />

        <individual-activity-tracker
            v-if="individualActivityTrackerState"
        />

        <platform-features-under-maintenance-popup
            v-if="pFeaturesUnderMPopupState"
        />
    </div>
</template>

<script>
/* eslint-disable */

import axios from 'axios';
import debounce from 'lodash/debounce';
import Push from 'push.js';
import { mapGetters } from 'vuex';

import audioPlay from '@/assets/audio/piano.mp3';
import { bus } from '@/main';
import commonSocketConnectMethod from '@/commonSocket';

import responsiveMixin from '@/mixins/responsive';
import currentUser from '@/mixins/currentUser';
import ChatInfoSection from
    '@/components/private/workspace/chat-info-section';
import ChatListSkeleton from './skeletons/ChatListSkeleton';
import ChatInfoSkeleton from './skeletons/ChatInfoSkeleton';

const ChatListHeader = () => import(
    '@/components/private/workspace/common/ChatListHeader'
);

const ChatList = () => import(
    '@/components/private/workspace/ui/ChatRoomList'
);

const CreateGroupTeams = () => import(
    '@/components/private/workspace/group/create-group-teams'
);

const IndividualActivityTracker = () => import(
    '@/components/private/workspace/individual-activity-tracker'
);

const AccessFeaturesForBlurb = () => import(
    '@/components/public/access-features-of-package-popup'
);

const PlatformFeaturesUnderMaintenancePopup = () => import(
    '@/components/common/platform-features-under-maintenance-popup'
);

const WelcomeWorkspaceBlurb = () => import(
    '@/components/private/workspace/common/WelcomeWorkspaceBlurb'
);

const UserBlockedPopup = () => import(
    '@/components/private/workspace/user-blocked-popup'
);

const ACTIVE_CHAT_ROOM_STORAGE_KEY = 'active_chat_room_id';
const MINIMUM_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_DELAY = 250;
const MOBILE_PANEL_TRANSITION_DELAY = 300;
const SIDEBAR_TRANSITION_DELAY = 360;

export default {
    name: 'ChatUserList',

    components: {
        ChatListHeader,
        ChatList,
        ChatListSkeleton,
        ChatInfoSkeleton,
        WelcomeWorkspaceBlurb,

        'user-chat-info': ChatInfoSection,
        'create-group-teams': CreateGroupTeams,
        'individual-activity-tracker': IndividualActivityTracker,
        'access-feature-of-blurb': AccessFeaturesForBlurb,
        'platform-features-under-maintenance-popup':
            PlatformFeaturesUnderMaintenancePopup,
        'user-blocked-popup': UserBlockedPopup
    },

    mixins: [
        responsiveMixin,
        currentUser
    ],

    props: {
        /*
         * Optional parent-owned request state. The Vuex loaded flag remains
         * the fallback, so existing Workspace usage stays compatible.
         */
        initialLoading: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            individualActivityTrackerState: false,
            connectionRequestCount: null,

            searchTerm: '',
            isSearchEnabled: false,
            userSearchListTabs: false,
            chatSearchItemListData: null,
            chatSearchItemList: [],
            searchingContactText: 'Looking for contact',

            typingUserInfoState: null,

            activeChatRoomId:
                localStorage.getItem(ACTIVE_CHAT_ROOM_STORAGE_KEY) || null,

            stableCachedRoom: null,

            isMobileChatRoomSelected: false,
            isMobileChatMounted: false,
            mobileChatCloseTimer: null,
            collapseActvToggleState: false,
            compactChatListState: false,
            headerCollapseState: false,
            requestedCollapseState: false,
            sidebarTransitionTimer: null,

            createGroupPopup: false,
            accessFeaturesOfPackageState: false,
            pFeaturesUnderMPopupState: false,

            blockedUserId: '',
            blockedUserEmail: '',
            blockSelectUser: false,

            chatListAPILoader: false,
            searchRequestId: 0,

            activeCallUserId: null,
            isCalling: false,

            debouncedSearch: null
        };
    },

    computed: {
        ...mapGetters([
            'normalChatRoomsGetters',
            'pinnedChatRoomsGetters',
            'isChatRoomLoaded'
        ]),

        canCreateTeamsAndGroups() {
            return Boolean(
                this.currentUser &&
                (this.hasProPayment || this.hasAutoPayment)
            );
        },

        pinnedChatRoomList() {
            const result = this.pinnedChatRoomsGetters;

            return result && Array.isArray(result.results)
                ? result.results
                : [];
        },

        fullChatRoomListResult() {
            const result = this.normalChatRoomsGetters;

            return result && Array.isArray(result.results)
                ? result.results
                : [];
        },

        fullChatListForLoadMore() {
            return (
                this.normalChatRoomsGetters &&
                this.normalChatRoomsGetters.next
            ) || '';
        },

        allChatRooms() {
            return [
                ...this.pinnedChatRoomList,
                ...this.fullChatRoomListResult
            ];
        },

        hasChatRooms() {
            return this.allChatRooms.length > 0;
        },

        isInitialChatLoading() {
            return (
                this.initialLoading ||
                !this.isChatRoomLoaded
            ) && !this.hasChatRooms;
        },

        searchResultLoaded() {
            return this.chatSearchItemListData !== null;
        },

        showSearchClearButton() {
            return Boolean(
                this.searchTerm.length > 2
            );
        },

        chatRoomInfo: {
            get() {
                if (!this.activeChatRoomId) {
                    return this.isMobile
                        ? null
                        : this.allChatRooms[0] || null;
                }

                const foundRoom = this.findChatRoomByRoomId(
                    this.activeChatRoomId
                );

                if (foundRoom) {
                    this.stableCachedRoom = foundRoom;
                    return foundRoom;
                }

                if (
                    this.stableCachedRoom &&
                    this.isSameRoomId(
                        this.stableCachedRoom.chat_room,
                        this.activeChatRoomId
                    )
                ) {
                    return this.stableCachedRoom;
                }

                if (this.isMobile) {
                    return null;
                }

                const fallbackRoom =
                    this.allChatRooms[0] || null;

                if (fallbackRoom) {
                    this.saveActiveChatRoom(fallbackRoom);
                }

                return fallbackRoom;
            },

            set(roomInfo) {
                if (
                    roomInfo &&
                    roomInfo.chat_room !== null &&
                    roomInfo.chat_room !== undefined
                ) {
                    this.saveActiveChatRoom(roomInfo);
                    return;
                }

                this.clearActiveChatRoom();
            }
        }
    },

    watch: {
        isChatRoomLoaded: {
            immediate: true,

            handler(loaded) {
                if (
                    loaded &&
                    !this.activeChatRoomId &&
                    !this.isMobile
                ) {
                    this.setInitialChatRoom();
                }
            }
        },

        allChatRooms() {
            this.ensureActiveRoomExists();
        },

        isMobile(isMobile) {
            this.clearMobileChatCloseTimer();
            this.clearSidebarTransitionTimer();
            this.isMobileChatRoomSelected = false;
            this.isMobileChatMounted = false;

            if (isMobile) {
                this.compactChatListState = false;
                this.headerCollapseState = false;
                return;
            }

            this.compactChatListState =
                this.collapseActvToggleState;
            this.headerCollapseState =
                this.collapseActvToggleState;
        }
    },

    created() {
        this.debouncedSearch = debounce(
            this.onChangeSearchUser,
            SEARCH_DEBOUNCE_DELAY
        );
    },

    mounted() {
        this.registerBusListeners();
        this.registerSocketListeners();

        if (this.isChatRoomLoaded) {
            this.setInitialChatRoom();
        }
    },

    beforeDestroy() {
        this.unregisterBusListeners();
        this.unregisterSocketListeners();
        this.clearMobileChatCloseTimer();
        this.clearSidebarTransitionTimer();

        if (
            this.debouncedSearch &&
            typeof this.debouncedSearch.cancel === 'function'
        ) {
            this.debouncedSearch.cancel();
        }

        this.toggleModalBodyScroll(false);
    },

    methods: {
        isSameRoomId(firstRoomId, secondRoomId) {
            if (
                firstRoomId === null ||
                firstRoomId === undefined ||
                secondRoomId === null ||
                secondRoomId === undefined
            ) {
                return false;
            }

            return String(firstRoomId) === String(secondRoomId);
        },

        saveActiveChatRoom(roomInfo) {
            if (
                !roomInfo ||
                roomInfo.chat_room === null ||
                roomInfo.chat_room === undefined
            ) {
                return;
            }

            this.activeChatRoomId = roomInfo.chat_room;
            this.stableCachedRoom = roomInfo;

            localStorage.setItem(
                ACTIVE_CHAT_ROOM_STORAGE_KEY,
                String(roomInfo.chat_room)
            );
        },

        clearActiveChatRoom() {
            this.activeChatRoomId = null;
            this.stableCachedRoom = null;
            this.clearMobileChatCloseTimer();
            this.isMobileChatRoomSelected = false;
            this.isMobileChatMounted = false;

            localStorage.removeItem(ACTIVE_CHAT_ROOM_STORAGE_KEY);
        },

        ensureActiveRoomExists() {
            if (!this.isChatRoomLoaded) {
                return;
            }

            if (!this.hasChatRooms) {
                this.clearActiveChatRoom();
                return;
            }

            if (!this.activeChatRoomId) {
                if (!this.isMobile) {
                    this.setInitialChatRoom();
                }

                return;
            }

            const activeRoom = this.findChatRoomByRoomId(
                this.activeChatRoomId
            );

            if (activeRoom) {
                this.stableCachedRoom = activeRoom;
                return;
            }

            if (
                this.stableCachedRoom &&
                this.isSameRoomId(
                    this.stableCachedRoom.chat_room,
                    this.activeChatRoomId
                )
            ) {
                return;
            }

            if (!this.isMobile) {
                this.saveActiveChatRoom(this.allChatRooms[0]);
            } else {
                this.clearActiveChatRoom();
            }
        },

        setInitialChatRoom() {
            if (this.isMobile || !this.hasChatRooms) {
                return;
            }

            const savedRoomId = localStorage.getItem(
                ACTIVE_CHAT_ROOM_STORAGE_KEY
            );

            if (savedRoomId) {
                const savedRoom = this.findChatRoomByRoomId(
                    savedRoomId
                );

                if (savedRoom) {
                    this.saveActiveChatRoom(savedRoom);
                    return;
                }
            }

            this.saveActiveChatRoom(this.allChatRooms[0]);
        },

        findChatRoomByUserId(userId) {
            return this.allChatRooms.find((item) => {
                return (
                    item &&
                    item.user &&
                    String(item.user.id) === String(userId)
                );
            }) || null;
        },

        findChatRoomByRoomId(roomId) {
            return this.allChatRooms.find((item) => {
                return (
                    item &&
                    this.isSameRoomId(item.chat_room, roomId)
                );
            }) || null;
        },

        registerBusListeners() {
            bus.$on(
                'typingEventForChatBus',
                this.handleTypingEvent
            );

            bus.$on(
                'userUpdateStatus',
                this.handleUserUpdateStatus
            );

            bus.$on(
                'userSelfStatus',
                this.handleUserSelfStatus
            );

            bus.$on(
                'messagesNotificationCount',
                this.handleMessagesNotificationCount
            );

            bus.$on(
                'unreadCountClearedForUserBus',
                this.handleUnreadCountCleared
            );

            bus.$on(
                'blockSelectUserBus',
                this.handleBlockSelectUserState
            );

            bus.$on(
                'createGroupPopupBus',
                this.handleCreateGroupPopupState
            );

            bus.$on(
                'selfFullChatListOrdChangedBus',
                this.handleChatListOrderChanged
            );

            bus.$on(
                'collapseActvToggleStateBus',
                this.handleCollapseState
            );

            bus.$on(
                'accessFeaturesOfPackageStateBus',
                this.handlePackagePopupState
            );

            bus.$on(
                'individualActivityTrackerStateBus',
                this.handleTrackerPopupState
            );

            bus.$on(
                'hideChatSectionInMblBus',
                this.handleHideMobileChatSection
            );

            bus.$on(
                'selfUserUpdateStatus',
                this.handleConnectionRequestCount
            );

            bus.$on(
                'pFeaturesUnderMPopupStateBus',
                this.handleMaintenancePopupState
            );

            bus.$on(
                'roomClearedBus',
                this.handleRoomCleared
            );
        },

        unregisterBusListeners() {
            bus.$off(
                'typingEventForChatBus',
                this.handleTypingEvent
            );

            bus.$off(
                'userUpdateStatus',
                this.handleUserUpdateStatus
            );

            bus.$off(
                'userSelfStatus',
                this.handleUserSelfStatus
            );

            bus.$off(
                'messagesNotificationCount',
                this.handleMessagesNotificationCount
            );

            bus.$off(
                'unreadCountClearedForUserBus',
                this.handleUnreadCountCleared
            );

            bus.$off(
                'createGroupPopupBus',
                this.handleCreateGroupPopupState
            );

            bus.$off(
                'blockSelectUserBus',
                this.handleBlockSelectUserState
            );

            bus.$off(
                'selfFullChatListOrdChangedBus',
                this.handleChatListOrderChanged
            );

            bus.$off(
                'collapseActvToggleStateBus',
                this.handleCollapseState
            );

            bus.$off(
                'accessFeaturesOfPackageStateBus',
                this.handlePackagePopupState
            );

            bus.$off(
                'individualActivityTrackerStateBus',
                this.handleTrackerPopupState
            );

            bus.$off(
                'hideChatSectionInMblBus',
                this.handleHideMobileChatSection
            );

            bus.$off(
                'selfUserUpdateStatus',
                this.handleConnectionRequestCount
            );

            bus.$off(
                'pFeaturesUnderMPopupStateBus',
                this.handleMaintenancePopupState
            );

            bus.$off(
                'roomClearedBus',
                this.handleRoomCleared
            );
        },

        registerSocketListeners() {
            commonSocketConnectMethod.$on(
                'videoCallAcceptedNotification',
                this.getAnswerOfVideoCallMethod
            );

            commonSocketConnectMethod.$on(
                'videoCallDeclinedNotification',
                this.resetVideoCallState
            );

            commonSocketConnectMethod.$on(
                'videoCallAlertPopupClosed',
                this.resetVideoCallState
            );

            commonSocketConnectMethod.$on(
                'autoMissedCallEvent',
                this.resetVideoCallState
            );
        },

        unregisterSocketListeners() {
            commonSocketConnectMethod.$off(
                'videoCallAcceptedNotification',
                this.getAnswerOfVideoCallMethod
            );

            commonSocketConnectMethod.$off(
                'videoCallDeclinedNotification',
                this.resetVideoCallState
            );

            commonSocketConnectMethod.$off(
                'videoCallAlertPopupClosed',
                this.resetVideoCallState
            );

            commonSocketConnectMethod.$off(
                'autoMissedCallEvent',
                this.resetVideoCallState
            );
        },

        handleCreateGroupPopupState(state) {
            this.createGroupPopup = Boolean(state);
        },

        handleBlockSelectUserState(state) {
            this.blockSelectUser = Boolean(state);
        },

        blockedUserMethod(
            id,
            firstName,
            lastName
        ) {
            this.blockedUserId = id;

            this.blockedUserEmail = [
                firstName,
                lastName
            ].filter(Boolean).join(' ');

            this.blockSelectUser = true;

            document.body.classList.add(
                'body_right_scroll_remove'
            );
        },

        handleChatListOrderChanged(roomId) {
            if (roomId) {
                this.moveToTopIndexSelectedUserOfLiveChatMethod(
                    roomId
                );
            }
        },

        handleCollapseState(state) {
            const shouldCollapse = Boolean(state);

            this.requestedCollapseState = shouldCollapse;
            this.clearSidebarTransitionTimer();
            this.headerCollapseState =
                !this.isMobile && shouldCollapse;

            if (this.isMobile) {
                this.collapseActvToggleState = shouldCollapse;
                this.compactChatListState = false;
                return;
            }

            if (shouldCollapse) {
                /*
                 * Start shrinking first. Keeping the expanded child layout
                 * mounted lets the sidebar clip its content progressively
                 * instead of leaving an empty wide panel.
                 */
                this.collapseActvToggleState = true;

                if (this.prefersReducedMotion()) {
                    this.compactChatListState = true;
                    return;
                }

                this.sidebarTransitionTimer = setTimeout(() => {
                    this.finishSidebarCollapse();
                }, SIDEBAR_TRANSITION_DELAY);

                return;
            }

            /*
             * Restore the expanded child layout while it is still clipped
             * inside 82px. On the next frame the width grows and naturally
             * reveals the content.
             */
            this.compactChatListState = false;

            this.$nextTick(() => {
                this.runOnNextFrame(() => {
                    if (!this.requestedCollapseState) {
                        this.collapseActvToggleState = false;
                    }
                });
            });
        },

        handleSidebarTransitionEnd(event) {
            if (
                !event ||
                event.target !== event.currentTarget ||
                event.propertyName !== 'width'
            ) {
                return;
            }

            if (this.collapseActvToggleState) {
                this.finishSidebarCollapse();
            }
        },

        finishSidebarCollapse() {
            this.clearSidebarTransitionTimer();

            if (
                !this.isMobile &&
                this.requestedCollapseState &&
                this.collapseActvToggleState
            ) {
                this.compactChatListState = true;
            }
        },

        clearSidebarTransitionTimer() {
            if (this.sidebarTransitionTimer === null) {
                return;
            }

            clearTimeout(this.sidebarTransitionTimer);
            this.sidebarTransitionTimer = null;
        },

        runOnNextFrame(callback) {
            if (
                typeof window !== 'undefined' &&
                typeof window.requestAnimationFrame === 'function'
            ) {
                window.requestAnimationFrame(callback);
                return;
            }

            setTimeout(callback, 0);
        },

        prefersReducedMotion() {
            return Boolean(
                typeof window !== 'undefined' &&
                typeof window.matchMedia === 'function' &&
                window.matchMedia(
                    '(prefers-reduced-motion: reduce)'
                ).matches
            );
        },

        handlePackagePopupState(state) {
            this.accessFeaturesOfPackageState = Boolean(state);
        },

        handleTrackerPopupState(state) {
            this.individualActivityTrackerState = Boolean(state);
        },

        clearMobileChatCloseTimer() {
            if (this.mobileChatCloseTimer === null) {
                return;
            }

            clearTimeout(this.mobileChatCloseTimer);
            this.mobileChatCloseTimer = null;
        },

        handleHideMobileChatSection(shouldHide) {
            if (!shouldHide || !this.isMobile) {
                return;
            }

            this.clearMobileChatCloseTimer();
            this.isMobileChatRoomSelected = false;

            /*
             * Keep ChatInfoSection mounted until the slide-out finishes.
             * It is then destroyed cleanly so reopening the same room
             * creates a fresh socket/message lifecycle.
             */
            this.mobileChatCloseTimer = setTimeout(() => {
                if (!this.isMobileChatRoomSelected) {
                    this.isMobileChatMounted = false;
                }

                this.mobileChatCloseTimer = null;
            }, MOBILE_PANEL_TRANSITION_DELAY);
        },

        handleConnectionRequestCount(data) {
            this.connectionRequestCount = data || null;
        },

        handleMaintenancePopupState(state) {
            this.pFeaturesUnderMPopupState = Boolean(state);
        },

        handleRoomCleared(shouldClearRoom) {
            if (!shouldClearRoom) {
                return;
            }

            this.clearActiveChatRoom();
        },

        handleTypingEvent(data) {
            if (!data || data.message === 'stop') {
                this.typingUserInfoState = null;
                return;
            }

            this.typingUserInfoState = data;
        },

        handleUserUpdateStatus(data) {
            if (!data || data.userID === undefined) {
                return;
            }

            const targetRoom = this.findChatRoomByUserId(
                data.userID
            );

            if (targetRoom && targetRoom.user) {
                this.$store.commit(
                    'UPDATE_CHAT_USER_ONLINE_STATUS',
                    data
                );
            }
        },

        handleUserSelfStatus(data) {
            if (!data || data.userID === undefined) {
                return;
            }

            const targetRoom = this.findChatRoomByUserId(
                data.userID
            );

            if (targetRoom && targetRoom.user) {
                this.$store.commit(
                    'UPDATE_CHAT_USER_CUSTOM_STATUS',
                    data
                );
            }
        },

        handleMessagesNotificationCount(data) {
            if (!data || !data.chatRoomId) {
                return;
            }

            const selectedRoomId = this.chatRoomInfo
                ? this.chatRoomInfo.chat_room
                : null;

            if (
                !this.isSameRoomId(
                    data.chatRoomId,
                    selectedRoomId
                )
            ) {
                this.handleChatRoomNotification(data);
            }
        },

        handleUnreadCountCleared(shouldClear) {
            if (!shouldClear || !this.chatRoomInfo) {
                return;
            }

            this.$store.commit(
                'CLEAR_CHAT_ROOM_UNREAD_COUNT',
                this.chatRoomInfo.chat_room
            );
        },

        handleChatRoomNotification(data) {
            const targetRoom = this.findChatRoomByRoomId(
                data.chatRoomId
            );

            if (!targetRoom) {
                return;
            }

            this.moveToTopIndexSelectedUserOfLiveChatMethod(
                data.chatRoomId
            );

            this.handleNotification(targetRoom, data);
        },

        handleNotification(room, data) {
            const unreadByRoom = data.unreadByRoom || {};
            const count = Number(
                unreadByRoom[room.chat_room] || 0
            );

            if (count <= 0) {
                return;
            }

            this.$store.commit(
                'UPDATE_CHAT_ROOM_UNREAD_COUNT',
                {
                    roomId: room.chat_room,
                    count
                }
            );

            if (Push.Permission.has()) {
                this.showPushNotification(data);
                return;
            }

            this.playNotificationSound(audioPlay);
        },

        stripHtml(value) {
            if (!value) {
                return '';
            }

            return String(value)
                .replace(/<[^>]*>/g, '')
                .replace(/\s+/g, ' ')
                .trim();
        },

        showPushNotification(data) {
            if (Push.Permission.get() !== 'granted') {
                Push.Permission.request();
                return;
            }

            const message = data.message
                ? this.stripHtml(data.message)
                : 'You received a new message';

            Push.create(
                data.senderName || 'New Notification',
                {
                    body: message,
                    icon: `${window.location.origin}/favicon.ico`,
                    timeout: 3000,
                    silent: false,

                    onClick() {
                        window.focus();

                        if (
                            window.location.pathname !== '/workspace/'
                        ) {
                            window.location.href = '/workspace/';
                        }

                        this.close();
                    }
                }
            );
        },

        playNotificationSound(sound) {
            if (!sound) {
                return;
            }

            const audio = new Audio(sound);

            audio.play().catch(() => {
                // Browser can block autoplay.
            });
        },

        handleSearchbarFocus() {
            this.isSearchEnabled = true;
        },

        async onChangeSearchUser() {
            const searchValue = this.searchTerm.trim();

            if (searchValue.length < MINIMUM_SEARCH_LENGTH) {
                this.clearSearchState();
                return;
            }

            const currentRequestId = ++this.searchRequestId;

            this.userSearchListTabs = true;
            this.chatSearchItemListData = null;
            this.chatSearchItemList = [];

            try {
                const response = await this.$store.dispatch(
                    'searchWorkspaceUser',
                    {
                        search_term: searchValue
                    }
                );

                if (currentRequestId !== this.searchRequestId) {
                    return;
                }

                const responseData = response && response.data
                    ? response.data
                    : {};

                this.chatSearchItemListData = responseData;

                this.chatSearchItemList = Array.isArray(
                    responseData.results
                )
                    ? responseData.results
                    : [];
            } catch (error) {
                if (currentRequestId !== this.searchRequestId) {
                    return;
                }

                this.chatSearchItemListData = {};
                this.chatSearchItemList = [];
            }
        },

        clearSearchState() {
            this.searchRequestId += 1;
            this.userSearchListTabs = false;
            this.chatSearchItemList = [];
            this.chatSearchItemListData = null;
        },

        searchBarForChatClosedFun() {
            if (
                this.debouncedSearch &&
                typeof this.debouncedSearch.cancel === 'function'
            ) {
                this.debouncedSearch.cancel();
            }

            this.clearSearchState();
            this.searchTerm = '';
            this.isSearchEnabled = false;
        },

        outsideClickOfSearchFieldMethod() {
            if (!this.searchTerm) {
                this.searchBarForChatClosedFun();
            }
        },

        async handleScroll() {
            const chatListElement = this.$refs.chatList;

            if (
                !chatListElement ||
                this.chatListAPILoader ||
                !this.fullChatListForLoadMore
            ) {
                return;
            }

            const scrollPosition =
                chatListElement.scrollTop +
                chatListElement.clientHeight;

            const isNearBottom =
                scrollPosition >=
                chatListElement.scrollHeight - 10;

            if (!isNearBottom) {
                return;
            }

            this.chatListAPILoader = true;

            try {
                const response = await axios.get(
                    this.fullChatListForLoadMore
                );

                if (response && response.data) {
                    await this.$store.dispatch(
                        'loadMoreLeftChatListDetails',
                        response.data
                    );
                }
            } catch (error) {
                // Load-more failure should not break the chat list.
            } finally {
                this.chatListAPILoader = false;
            }
        },

        selectChatRoomForMessageMethod(roomInfo) {
            if (
                !roomInfo ||
                roomInfo.chat_room === null ||
                roomInfo.chat_room === undefined
            ) {
                return;
            }

            const isAlreadySelected = this.isSameRoomId(
                this.activeChatRoomId,
                roomInfo.chat_room
            );

            if (isAlreadySelected) {
                this.stableCachedRoom = roomInfo;
            } else {
                this.chatRoomInfo = roomInfo;
            }

            // if (this.userSearchListTabs) {
            //     this.searchBarForChatClosedFun();
            // }

            this.openMobileChatRoom();
        },

        searchingChatRoomForMessageMethod(roomInfo) {
            this.selectChatRoomForMessageMethod(roomInfo);
        },

        openMobileChatRoom() {
            if (
                !this.isMobile ||
                this.isMobileChatRoomSelected
            ) {
                return;
            }

            this.clearMobileChatCloseTimer();
            this.isMobileChatMounted = true;

            /*
             * Let Vue mount ChatInfoSection in its off-screen position,
             * then activate the transform on the next paint.
             */
            this.$nextTick(() => {
                if (window.requestAnimationFrame) {
                    window.requestAnimationFrame(() => {
                        this.isMobileChatRoomSelected = true;
                    });

                    return;
                }

                setTimeout(() => {
                    this.isMobileChatRoomSelected = true;
                }, 0);
            });
        },

        moveToTopIndexSelectedUserOfLiveChatMethod(roomId) {
            if (
                roomId === null ||
                roomId === undefined
            ) {
                return;
            }

            this.$store.commit(
                'MOVE_CHAT_ROOM_TO_TOP',
                roomId
            );
        },

        handleSearchVideoCall(data) {
            if (!data) {
                return;
            }

            this.videoCallRequestMethod(
                data.userId,
                data.roomId
            );
        },

        videoCallRequestMethod(targetUserId, roomId) {
            if (
                this.isCalling ||
                targetUserId === null ||
                targetUserId === undefined ||
                roomId === null ||
                roomId === undefined
            ) {
                return;
            }

            this.isCalling = true;
            this.activeCallUserId = targetUserId;

            const firstName =
                this.currentUser &&
                this.currentUser.first_name
                    ? this.currentUser.first_name
                    : '';

            const lastName =
                this.currentUser &&
                this.currentUser.last_name
                    ? this.currentUser.last_name
                    : '';

            const callerName = `${firstName} ${lastName}`.trim();

            commonSocketConnectMethod.emit({
                callerName,
                type: 'videoCalling',
                callerID: this.selfUserId,
                targetId: targetUserId,
                roomId
            });
        },

        resetVideoCallState() {
            this.isCalling = false;
            this.activeCallUserId = null;
        },

        getAnswerOfVideoCallMethod(answerData) {
            if (!answerData) {
                return;
            }

            if (
                String(this.selfUserId) !==
                String(answerData.targetId)
            ) {
                return;
            }

            this.resetVideoCallState();

            this.$router.push(
                `/video/conference/#${answerData.roomId}`
            );
        },

        toggleModalBodyScroll(shouldLock) {
            document.body.classList.toggle(
                'body_right_scroll_remove',
                Boolean(shouldLock)
            );
        },

        createGroupPopupEnabledFun() {
            this.createGroupPopup = true;
        },

        accessFeaturesOfPackageMethod() {
            this.accessFeaturesOfPackageState = true;
            this.toggleModalBodyScroll(true);
        },

        individualActivityTrackerOpenMethod() {
            this.individualActivityTrackerState = true;
            this.toggleModalBodyScroll(true);
        },

        mobileToolbarEnabledMethod() {
            this.$emit('open-mobile-tools');
        },

        pFeaturesUnderMPopupMethod() {
            this.pFeaturesUnderMPopupState = true;
            this.toggleModalBodyScroll(true);
        }
    }
};
</script>

<style lang="scss" scoped>
.workspace_main_content {
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
    min-width: 0;
    overflow: hidden;
}

/* Fixed desktop chat-list width */
.workspace_main_content > #sidepanel {
    display: flex;
    width: 45%;
    min-width: 45%;
    max-width: 45%;
    height: 100%;
    overflow: hidden;
    color: #f5f5f5;
    background: #111111;
    flex: 0 0 45%;
    flex-direction: column;
    transition:
        width 320ms cubic-bezier(0.4, 0, 0.2, 1),
        min-width 320ms cubic-bezier(0.4, 0, 0.2, 1),
        max-width 320ms cubic-bezier(0.4, 0, 0.2, 1),
        flex-basis 320ms cubic-bezier(0.4, 0, 0.2, 1);
}

/* Center chat section uses remaining width */
.workspace_main_content >
    .workspace_chat_info_panel {
    flex: 1 1 auto;
    width: auto !important;
    min-width: 0;
    height: 100%;
    overflow: hidden;
}


/* Search */

.user_search_for_new_chat {
    max-height: 80px;
    flex-shrink: 0;
    padding: 10px;
    overflow: hidden;
    opacity: 1;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
    background: #111111;
    transition:
        max-height 220ms cubic-bezier(0.4, 0, 0.2, 1),
        padding-top 220ms cubic-bezier(0.4, 0, 0.2, 1),
        padding-bottom 220ms cubic-bezier(0.4, 0, 0.2, 1),
        opacity 140ms ease,
        border-color 140ms ease;

    .search_for_new_chat_inner {
        position: relative;
        display: flex;
        align-items: center;
        min-height: 44px;
        overflow: hidden;
        border: 2px solid rgba(255, 255, 255, 0.15);
        border-radius: 24px;
        background: rgba(255, 255, 255, 0.03);
        transition:
            border-color 0.2s ease,
            background-color 0.2s ease;
    }

    .search_icon {
        position: absolute;
        left: 13px;
        top: 50%;
        display: inline-flex;
        color: rgba(255, 255, 255, 0.72);
        pointer-events: none;
        transform: translateY(-50%);
    }

    .search_icon_btn {
        position: absolute;
        top: 50%;
        z-index: 2;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        border: 0;
        border-radius: 50%;
        background: transparent;
        outline: none;
        cursor: pointer;
        transform: translateY(-50%);
        transition:
            color 0.2s ease,
            background-color 0.2s ease;

        &:hover,
        &:focus-visible {
            background: rgba(255, 255, 255, 0.08);
        }
    }

    .search_back_btn {
        left: 6px;
        display: none;
        color: #ffd400;
    }

    .search_clear_btn {
        right: 8px;
        color: #f44336;

        &:hover,
        &:focus-visible {
            color: #87cefa;
        }
    }

    .form-control {
        width: 100%;
        height: 42px;
        padding: 8px 45px;
        border: 0;
        background: transparent;
        color: #ffd400;
        font-family:
            "proxima-nova",
            "Source Sans Pro",
            sans-serif;
        font-size: 15px;
        line-height: 1.4;
        outline: none;
        box-shadow: none;

        &::placeholder {
            color: rgba(255, 255, 255, 0.55);
        }

        &::-webkit-search-cancel-button {
            display: none;
        }
    }

    &.search_enabled {
        .search_for_new_chat_inner {
            border-color: #ffd400;
            background: rgba(255, 212, 0, 0.03);
        }

        .search_icon {
            display: none;
        }

        .search_back_btn {
            display: inline-flex;
        }
    }
}

/* Chat list */
#contacts_list {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
        background: rgba(0, 0, 0, 0.08);
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 5px;
        background: #323232;
    }
}

.user_list_for_chat {
    ul {
        display: flex;
        flex-wrap: wrap;
        margin: 0;
        padding: 0;

        li.chat_user_listed {
            width: 100%;
            flex: 0 0 100%;
            order: 100;
            transition: order 0.4s cubic-bezier(0.4, 0, 1, 1);

            &:hover {
                .status_tootip_wrp {
                    visibility: visible;
                    opacity: 1;
                }
            }
        }
    }
}

.workspace_chat_list_header {
    position: relative;
    z-index: 20;
    display: block;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    flex: 0 0 auto;
}

.chat-list-header-mode-enter-active,
.chat-list-header-mode-leave-active {
    transition:
        opacity 90ms ease,
        transform 90ms cubic-bezier(0.4, 0, 0.2, 1);
}

.chat-list-header-mode-enter,
.chat-list-header-mode-leave-to {
    opacity: 0;
    transform: translate3d(-5px, 0, 0);
}

/* Empty and loading states */

.chat_list_initial_loader,
.chat_list_empty_state {
    display: flex;
    align-items: center;
    justify-content: center;
}

.chat_list_initial_loader {
    flex: 1;
    min-height: 0;
}

.chat_list_empty_state {
    min-height: 120px;
    padding: 24px 16px;
    color: rgba(255, 255, 255, 0.55);
    font-family:
        "MyriadProRegular",
        "Lato",
        sans-serif;
    font-size: 14px;
    text-align: center;

    &--full {
        flex: 1;
    }
}

.chat_list_loader {
    width: 28px;
    height: 28px;
    margin: 16px auto;
    border: 3px solid rgba(255, 255, 255, 0.18);
    border-top-color: #ffd400;
    border-radius: 50%;
    animation: chat-list-spin 0.8s linear infinite;
}

@keyframes chat-list-spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* Existing chat-list helpers */

.typing_info_text {
    display: block;
    color: #00de09;
    font-family:
        "MyriadProRegular",
        "Lato",
        sans-serif;
    font-size: 14px;
}

/* Desktop collapsed chat list */

.workspace_main_content.collapsed_chat_list >
    #sidepanel {
    position: relative;
    z-index: 30;
    width: 82px;
    min-width: 82px;
    max-width: 82px;
    overflow: hidden;
    flex-basis: 82px;
}

.workspace_main_content.collapsed_chat_list.compact_chat_list >
    #sidepanel {
    overflow: visible;
}

.workspace_main_content.collapsed_chat_list {
    .user_search_for_new_chat {
        max-height: 0;
        padding-top: 0;
        padding-bottom: 0;
        pointer-events: none;
        opacity: 0;
        border-bottom-color: transparent;
    }
}

.workspace_main_content.compact_chat_list {
    .user_search_for_new_chat {
        visibility: hidden;
    }

    .workspace_chat_list_header {
        width: 82px;
        min-width: 82px;
        max-width: 82px;
    }

    #contacts_list {
        width: 82px;
        overflow-x: hidden;
    }
}

@media only screen and (max-width: 767px) {
    .workspace_main_content {
        position: relative;
        display: block;
    }

    .workspace_main_content > #sidepanel,
    .workspace_main_content >
        .workspace_chat_info_panel {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: block !important;
        width: 100% !important;
        min-width: 0;
        max-width: none;
        height: 100%;
        backface-visibility: hidden;
        transition:
            transform 280ms
            cubic-bezier(0.4, 0, 0.2, 1);
    }

    .workspace_main_content > #sidepanel {
        z-index: 1;
        transform: translate3d(0, 0, 0);
    }

    .workspace_main_content >
        .workspace_chat_info_panel {
        z-index: 2;
        pointer-events: none;
        transform: translate3d(100%, 0, 0);
    }

    .workspace_main_content.mobile_chat_open >
        #sidepanel {
        pointer-events: none;
        transform: translate3d(-100%, 0, 0);
    }

    .workspace_main_content.mobile_chat_open >
        .workspace_chat_info_panel {
        pointer-events: auto;
        transform: translate3d(0, 0, 0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .chat-list-header-mode-enter-active,
    .chat-list-header-mode-leave-active,
    .workspace_main_content > #sidepanel,
    .workspace_main_content >
        .workspace_chat_info_panel {
        transition: none;
    }
}
</style>

<!--
    ChatListHeader is a child component. This unscoped rule is intentional:
    its dropdown must escape the 82px collapsed sidebar without being clipped.
-->
<style lang="scss">
.workspace_main_content.compact_chat_list {
    .workspace_chat_list_header {
        .chat_menu_list {
            right: auto;
            left: calc(100% + 8px);
            z-index: 1000;
            width: 190px;
            min-width: 190px;
            max-width: calc(100vw - 115px);
        }
    }
}
</style>
